// Действия цикла: типы, участники, расчёт, методы выполнения.
// Вынесено из api.ts (Фаза 1, разбор клиента по доменам).

import { request, LONG_ACTION_TIMEOUT_MS } from "./client";
import { type CampaignCycleOverview } from "./cycle";
import { type CampaignAvatar, type MediaAsset } from "./media";

export type CycleActionType =
  | "REQUEST_REPORT"
  | "INVESTIGATE"
  | "MEET_FACTION"
  | "PRESSURE_INSTITUTION"
  | "PUBLIC_STATEMENT"
  | "MOBILIZE_RESOURCE"
  | "CHECK_RUMOR"
  | "NEGOTIATE"
  | "FORCE_OPERATION"
  | "FREE_ACTION"
  | "PREPARE_INITIATIVE";
export type CycleActionVisibility = "PRIVATE" | "PUBLIC";
export type ActionOutcome = "SUCCESS" | "PARTIAL_SUCCESS" | "FAILURE";
export type ActionResultKind = "DONE" | "LEARNED" | "PREPARED" | "BLOCKED" | "NEEDS_JOINT_ACTION";
export type ActionRoleFit = "DIRECT" | "PARTIAL" | "OUTSIDE_ROLE" | "NEEDS_ALLY";
export type ActionConsequenceTiming = "IMMEDIATE" | "DEFERRED" | "MIXED";
export type ActionResolutionStatus =
  "RESOLVED" | "PENDING_DAY_END" | "PENDING_APPROVAL" | "DECLINED";
export type ActionCollaborationStatus =
  "SOLO" | "PENDING_APPROVAL" | "APPROVED" | "DECLINED";
export type ActionParticipantStatus = "PENDING" | "APPROVED" | "DECLINED";
export type ActionNarrativeStatus =
  "PENDING" | "PROCESSING" | "RETRY" | "READY";
export interface ActionFactor {
  code: string;
  label: string;
  value: number;
}
export interface CycleActionKnowledgeFinding {
  id: string;
  code: string;
  category: string;
  title: string;
  concreteText: string;
  evidenceText: string;
  actionHintText: string;
}
export interface CycleActionParticipant {
  userId: string;
  campaignMemberId: string;
  displayName: string;
  avatar: CampaignAvatar;
  status: ActionParticipantStatus;
  initiator: boolean;
}
export interface CycleActionInitiativeSuggestion {
  title: string;
  description: string;
  intendedOutcome: string;
}
export interface ActionGuidance {
  resultKind: ActionResultKind;
  resultKindLabel: string;
  roleFit: ActionRoleFit;
  roleFitLabel: string;
  whatHappenedText: string;
  whatChangedText: string;
  whatNextText: string;
  initiativePolicyText: string;
}
export interface ActionCalculation {
  successChance?: number;
  rollValue: number;
  partialSuccessLimit?: number;
  positiveFactors: ActionFactor[];
  negativeFactors: ActionFactor[];
}
export interface CycleAction {
  id: string;
  actorUserId: string;
  actorCampaignMemberId: string;
  actorDisplayName: string;
  actorAvatar: CampaignAvatar;
  actionType: CycleActionType;
  visibility: CycleActionVisibility;
  targetCode?: string;
  targetDisplayName?: string;
  description: string;
  intentSummary: string;
  methodSummary: string;
  toneSummary: string;
  expectedResultText: string;
  outcome: ActionOutcome;
  consequenceTiming: ActionConsequenceTiming;
  resolutionStatus: ActionResolutionStatus;
  resultText: string;
  knowledgeFindings: CycleActionKnowledgeFinding[];
  sceneText: string;
  achievementText: string;
  limitationText: string;
  followUpText: string;
  deferredOutcomeText: string;
  deferredReasonText: string;
  chronicleTitle: string;
  effectText: string;
  modifierValue: number;
  images: MediaAsset[];
  calculation: ActionCalculation;
  narrativeProvider: string;
  narrativeStatus: ActionNarrativeStatus;
  narrativeNextAttemptAt: string;
  collaborationStatus: ActionCollaborationStatus;
  participants: CycleActionParticipant[];
  canApprove: boolean;
  canDecline: boolean;
  requiresInitiative: boolean;
  initiativeSuggestion?: CycleActionInitiativeSuggestion;
  guidance: ActionGuidance;
  isMine: boolean;
  createdAt: string;
  resolvedAt?: string;
}

export const actionApi = {
  performCycleAction: (
    campaignId: string,
    payload: {
      actionType: CycleActionType;
      visibility: CycleActionVisibility;
      targetCode?: string;
      description: string;
      coActorUserIds?: string[];
    },
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/actions`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  setJointActionApproval: (
    campaignId: string,
    actionId: string,
    approve: boolean,
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/actions/${actionId}/approval`,
      { method: "POST", body: JSON.stringify({ approve }) },
    ),
  createInitiativeFromAction: (campaignId: string, actionId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/actions/${actionId}/initiative`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
};
